import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  TextField,
  Skeleton,
  Stack,
  Chip,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Button,
  Popover,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import { Check, ArrowLeft, Clock, Eye, X, Trash2, UserX, Search } from "react-feather";
import { useAuth } from "../../auth/workosAuth";
import { DataGrid, useGridApiContext, GridPagination } from "@mui/x-data-grid";
import axios from "../../utils/axios";
import { config } from "../../constants";
import { InlineEditCell } from "./InlineEditCell";

const AutoFilterMenu = React.forwardRef(function AutoFilterMenu(props, ref) {
  const apiRef = useGridApiContext();
  React.useEffect(() => {
    apiRef.current.hideColumnMenu();
    const panelEl = document.querySelector(".MuiDataGrid-panel");
    if (panelEl) {
      apiRef.current.hideFilterPanel();
    } else {
      apiRef.current.showFilterPanel(props.colDef?.field);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
});

const RecipientsFooter = () => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid rgba(224,224,224,1)" }}>
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pl: 1 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#e65100" }} />
      <Typography sx={{ fontSize: "0.6rem", color: "#e65100", fontWeight: 600 }}>Broker</Typography>
    </Stack>
    <GridPagination />
  </Box>
);

const autoRowHeight = () => "auto";
const getMissingContactRowClass = (params) => !params.row.contact_id ? 'missing-contact-row' : '';

const dataGridSx = {
  fontSize: "0.75rem",
  border: "none",
  "& .MuiDataGrid-columnHeaderTitle": { fontSize: "0.75rem", fontWeight: 600 },
  "& .MuiDataGrid-cell": { fontSize: "0.75rem", py: 0.25, display: "flex", alignItems: "center", minHeight: "28px !important", maxHeight: "28px !important" },
  "& .MuiDataGrid-row": { cursor: "pointer", minHeight: "28px !important", maxHeight: "28px !important" },
  "& .MuiDataGrid-columnHeaders": { minHeight: "32px !important", maxHeight: "32px !important" },
  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": { display: "none" },
  "& .MuiDataGrid-sortIcon": { display: "none" },
  "& .MuiDataGrid-columnSeparator": { visibility: "hidden" },
  "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator": { visibility: "visible" },
  "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
  "& .missing-contact-row": { bgcolor: "#ffebee" },
  "& .MuiTablePagination-toolbar": { alignItems: "center", minHeight: 40 },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.75rem", margin: 0 },
  "& .MuiTablePagination-select": { fontSize: "0.75rem" },
  "& .MuiDataGrid-selectedRowCount": { display: "none" },
  "& .MuiDataGrid-footerContainer": { minHeight: 40, maxHeight: 40 },
};

// InlineEditCell extracted to ./InlineEditCell.js

function CreateContactDialog({ open, onClose, row, onSave }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setName(""); setPhone(""); setEmail(""); setSaving(false); } }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(name, phone, email);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          overflow: "hidden",
          background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
          border: "1px solid rgba(0,0,0,0.08)",
        }
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        position="relative"
        padding="16px 24px"
        borderBottom="1px solid rgba(0,0,0,0.05)"
        sx={{ background: "rgba(235,245,255,0.6)" }}
      >
        <DialogTitle
          padding="0 !important"
          sx={{
            color: "#3f51b5",
            fontWeight: 800,
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            "::before": {
              content: '""',
              display: "inline-block",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#3f51b5",
              marginRight: "10px",
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(63, 81, 181, 0.7)" },
                "70%": { boxShadow: "0 0 0 10px rgba(63, 81, 181, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(63, 81, 181, 0)" }
              }
            }
          }}
        >
          Create Contact for {row?.customer_name}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={saving}
          sx={{
            position: "absolute",
            right: 16,
            top: 10,
            color: "#495057",
            "&:hover": {
              background: "rgba(0,0,0,0.05)",
              transform: "rotate(90deg)",
              transition: "transform 0.3s ease"
            }
          }}
        >
          <X size={16} />
        </IconButton>
      </Stack>
      <DialogContent sx={{ padding: "24px 28px" }}>
        <Stack spacing={2}>
          <TextField
            label="Name" size="small" fullWidth autoFocus
            value={name} onChange={(e) => setName(e.target.value)}
            disabled={saving}
            InputProps={{ sx: { fontSize: "0.85rem", borderRadius: "8px" } }}
            InputLabelProps={{ sx: { fontSize: "0.85rem" } }}
          />
          <TextField
            label="Phone" size="small" fullWidth
            value={phone} onChange={(e) => { const v = e.target.value; if (v === "" || /^[\d\-+() ]*$/.test(v)) setPhone(v); }}
            placeholder="+1234567890"
            disabled={saving}
            InputProps={{ sx: { fontSize: "0.85rem", borderRadius: "8px" } }}
            InputLabelProps={{ sx: { fontSize: "0.85rem" } }}
          />
          <TextField
            label="Email" size="small" fullWidth
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            disabled={saving}
            InputProps={{ sx: { fontSize: "0.85rem", borderRadius: "8px" } }}
            InputLabelProps={{ sx: { fontSize: "0.85rem" } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: "16px 24px", gap: "12px" }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s",
            padding: "8px 16px",
            color: "#5a6268",
            background: "linear-gradient(45deg, #f5f5f5, #e9ecef)",
            border: "1px solid #dee2e6",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              background: "linear-gradient(45deg, #e9ecef, #dee2e6)",
            }
          }}
        >
          <X size={14} style={{ marginRight: "6px" }} /> Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !name || (!phone && !email)}
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            padding: "8px 20px",
            transition: "all 0.2s",
            background: "linear-gradient(45deg, #1565c0, #1976d2)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(21,101,192,0.25)"
            }
          }}
        >
          {saving ? (
            <CircularProgress size={16} sx={{ color: "#fff" }} />
          ) : (
            <><Check size={14} style={{ marginRight: "6px" }} /> Save</>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ContactPickerPopover({ anchorEl, contacts, loading, onClose, onSelect, onCreateNew }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search) return contacts.slice(0, 50);
    const q = search.toLowerCase();
    return contacts.filter(c => (c.name || "").toLowerCase().includes(q)).slice(0, 50);
  }, [contacts, search]);

  // Reset search when popover opens
  useEffect(() => {
    if (anchorEl) setSearch("");
  }, [anchorEl]);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { sx: { width: 320, maxHeight: 400, display: "flex", flexDirection: "column" } } }}
    >
      <Box sx={{ p: 1, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search size={14} /></InputAdornment>
            ),
            sx: { fontSize: "0.75rem" },
          }}
        />
      </Box>
      <List dense sx={{ flex: 1, overflow: "auto", py: 0.5 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <>
            {filtered.map((c) => (
              <ListItemButton
                key={`${c.id}-${c.customer_id}`}
                onClick={() => onSelect(c)}
                sx={{ py: 0.5, px: 1.5 }}
              >
                <ListItemText
                  primary={c.name || "(unnamed)"}
                  secondary={c.phone || ""}
                  primaryTypographyProps={{ fontSize: "0.75rem", fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: "0.65rem" }}
                />
              </ListItemButton>
            ))}
            {!loading && contacts.length === 0 && (
              <Typography variant="body2" sx={{ px: 1.5, py: 1, fontSize: "0.7rem", color: "text.secondary" }}>
                No contacts found
              </Typography>
            )}
            {contacts.length > 0 && filtered.length === 0 && (
              <Typography variant="body2" sx={{ px: 1.5, py: 1, fontSize: "0.7rem", color: "text.secondary" }}>
                No matches
              </Typography>
            )}
          </>
        )}
      </List>
      <Divider />
      <Button
        size="small"
        fullWidth
        onClick={onCreateNew}
        sx={{ py: 0.75, fontSize: "0.7rem", textTransform: "none", borderRadius: 0 }}
      >
        + Create new contact
      </Button>
    </Popover>
  );
}

const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDatajoltUser = user?.email?.endsWith("@datajolt.io") || user?.email?.startsWith("maxime.broussard");

  const [campaign, setCampaign] = useState(null);
  const [priorityRank, setPriorityRank] = useState(8);
  const [kpis, setKpis] = useState(null);
  const [commodities, setCommodities] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [selectedCommodityIds, setSelectedCommodityIds] = useState({ type: "include", ids: new Set() });
  const [selectedRecipientIds, setSelectedRecipientIds] = useState({ type: "include", ids: new Set() });
  const [loading, setLoading] = useState(true);
  const [rankLoading, setRankLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showMissingPricesOnly, setShowMissingPricesOnly] = useState(false);
  const [showMissingContactsOnly, setShowMissingContactsOnly] = useState(false);

  // Price editing
  const [editingPrice, setEditingPrice] = useState(null);
  const [editPriceValue, setEditPriceValue] = useState("");

  // Recipient field editing (phone/email)
  const [editingRecipientField, setEditingRecipientField] = useState(null);
  const editingFieldRef = useRef(null);
  const updateEditingField = useCallback((val) => {
    editingFieldRef.current = val;
    setEditingRecipientField(val);
  }, []);

  // Preview state
  const [previewMessages, setPreviewMessages] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedRecipientName, setSelectedRecipientName] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Channel toggle per recipient
  const [recipientChannels, setRecipientChannels] = useState({});
  const [channelLoadingRows, setChannelLoadingRows] = useState(new Set());

  // Template selectors
  const [templates, setTemplates] = useState([]);
  const [smsTemplateId, setSmsTemplateId] = useState(null);
  const [emailTemplateId, setEmailTemplateId] = useState(null);

  // Create contact dialog
  const [createContactOpen, setCreateContactOpen] = useState(false);
  const [createContactRow, setCreateContactRow] = useState(null);

  // Contact picker popover
  const [contactPickerAnchor, setContactPickerAnchor] = useState(null);
  const [contactPickerRow, setContactPickerRow] = useState(null);
  const [allContacts, setAllContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  // Broker opt-out dialog
  const [brokerOptOutOpen, setBrokerOptOutOpen] = useState(false);
  const [brokerOptOutContactId, setBrokerOptOutContactId] = useState(null);

  // Fetch campaign details
  const fetchDetails = useCallback(async (rankOverride) => {
    try {
      const url = `${config.url.SMS_URL}/api/sms/campaigns/${id}/details` +
        (rankOverride != null ? `?priority_rank=${rankOverride}` : '');
      const res = await axios.get(url);
      setCampaign(res.data.campaign);
      setPriorityRank(res.data.campaign?.priority_rank || 8);
      setKpis(res.data.kpis);
      const coms = (res.data.commodities || []).map((c, i) => ({ ...c, id: i + 1 }));
      setCommodities(coms);
      setSelectedCommodityIds({ type: "include", ids: new Set(coms.filter(c => c.is_available !== false).map(c => c.id)) });
      const recs = (res.data.recipients || []).map((r, i) => ({ ...r, id: i + 1 }));
      setRecipients(recs);
      setSelectedRecipientIds({ type: "include", ids: new Set(recs.map(r => r.id)) });
      setPreviewMessages(res.data.messages || []);
      // Initialize template IDs from campaign
      setSmsTemplateId(res.data.campaign?.sms_template_id || res.data.campaign?.selected_template_id || null);
      setEmailTemplateId(res.data.campaign?.email_template_id || null);
      // Initialize channel preferences from recipients (brokers default to email)
      const channels = {};
      (res.data.recipients || []).forEach(r => {
        if (r.customer_name) channels[r.customer_name] = r.is_broker ? "email" : (r.preferred_channel || "sms");
      });
      setRecipientChannels(channels);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load campaign details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${config.url.SMS_URL}/api/sms/templates`);
        setTemplates(res.data.templates || res.data || []);
      } catch (err) {
        console.error("Failed to load templates:", err);
      }
    };
    fetchTemplates();
  }, []);

  const handlePriorityRankChange = async (newRank) => {
    setPriorityRank(newRank);
    setRankLoading(true);
    try {
      await axios.patch(`${config.url.SMS_URL}/api/sms/campaigns/${id}/priority-rank`, { priority_rank: newRank });
      await fetchDetails(newRank);
    } catch {
      toast.error("Failed to update priority rank");
    } finally {
      setRankLoading(false);
    }
  };

  // Fetch preview from campaign_id (all state read from DB)
  const fetchPreview = useCallback(async () => {
    if (!campaign) return;
    setPreviewLoading(true);
    try {
      const response = await axios.post(
        `${config.url.SMS_URL}/api/sms/campaigns/${id}/preview`,
        { recipient_channels: recipientChannels }
      );
      setPreviewMessages(response.data.messages || []);
    } catch (err) {
      console.error("Preview load error:", err);
    } finally {
      setPreviewLoading(false);
    }
  }, [campaign, id, recipientChannels]);

  const handleTemplateChange = async (field, value) => {
    const newVal = value || null;
    if (field === "sms") {
      setSmsTemplateId(newVal);
    } else {
      setEmailTemplateId(newVal);
    }
    try {
      const body = field === "sms"
        ? { sms_template_id: newVal, selected_template_id: newVal }
        : { email_template_id: newVal };
      await axios.patch(`${config.url.SMS_URL}/api/sms/campaigns/${id}/templates`, body);
      fetchPreview();
    } catch {
      toast.error("Failed to update template");
    }
  };

  // Fetch/reload preview when campaign changes (skip initial load — fetchDetails already returns messages)
  const initialLoad = useRef(true);
  useEffect(() => {
    if (!campaign) return;
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    fetchPreview();
  }, [campaign]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSavePrice = async (commodityName) => {
    try {
      await axios.post(`${config.url.SMS_URL}/api/sms/commodities/override`, {
        name: commodityName,
        price: editPriceValue ? parseFloat(editPriceValue) : null,
        is_available: true,
        campaign_id: parseInt(id),
      });
      setCommodities(prev =>
        prev.map(c => c.name === commodityName ? { ...c, price: editPriceValue ? parseFloat(editPriceValue) : null } : c)
      );
      setEditingPrice(null);
      setEditPriceValue("");
      toast.success("Price updated");
      fetchPreview();
    } catch {
      toast.error("Failed to update price");
    }
  };

  const handleSaveRecipientField = async (customerName, field, value) => {
    if (!customerName) return;
    if (field === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error("Invalid email address");
      return;
    }
    const prevRecipients = recipients;
    const row = recipients.find(r => r.customer_name === customerName);

    // Optimistic update + close editor
    setRecipients(prev => prev.map(r => r.customer_name === customerName ? { ...r, [field]: value || null } : r));
    updateEditingField(null);

    try {
      await axios.post(`${config.url.SMS_URL}/api/sms/customers/override-data`, {
        customer_name: customerName,
        customer_id: row?.customer_id,
        contact_id: row?.contact_id || null,
        [field]: value || null,
      });
    } catch {
      setRecipients(prevRecipients);
      toast.error(`Failed to update ${field === 'phone' ? 'phone' : field === 'email' ? 'email' : 'contact name'}`);
    }
  };

  const handleCreateContact = async (name, phone, email) => {
    if (!createContactRow) return;
    const prevRecipients = recipients;
    const { customer_name, customer_id } = createContactRow;

    // Close dialog and optimistically update
    setCreateContactOpen(false);
    setRecipients(prev => prev.map(r =>
      r.customer_name === customer_name && r.customer_id === customer_id
        ? { ...r, person_name: name, phone: phone || r.phone, email: email || r.email, contact_id: "pending" }
        : r
    ));

    try {
      await axios.post(`${config.url.SMS_URL}/api/sms/contacts`, {
        customer_name, customer_id, name, phone, email, is_primary: true,
      });
      fetchDetails();
      fetchPreview();
    } catch (err) {
      setRecipients(prevRecipients);
      toast.error(err.response?.data?.error || "Failed to create contact");
    }
  };

  const handleUnassignContact = async (contactId, customerId) => {
    const prevRecipients = recipients;

    // Optimistically clear contact fields
    setRecipients(prev => prev.map(r =>
      r.contact_id === contactId && r.customer_id === customerId
        ? { ...r, contact_id: null, person_name: null, phone: null, email: null }
        : r
    ));

    try {
      await axios.delete(`${config.url.SMS_URL}/api/sms/contacts/${contactId}/customer/${customerId}`);
      fetchDetails();
      fetchPreview();
    } catch {
      setRecipients(prevRecipients);
      toast.error("Failed to unassign contact");
    }
  };

  const handleOptOut = async (contactId, isBroker) => {
    if (isBroker) {
      setBrokerOptOutContactId(contactId);
      setBrokerOptOutOpen(true);
    } else {
      try {
        await axios.post(`${config.url.SMS_URL}/api/sms/contacts/opt-out`, { contact_id: contactId });
        toast.success("Contact opted out");
        fetchDetails();
      } catch {
        toast.error("Failed to opt out contact");
      }
    }
  };

  const handleBrokerOptOutConfirm = async (optOutAll) => {
    try {
      await axios.post(`${config.url.SMS_URL}/api/sms/contacts/opt-out`, {
        contact_id: brokerOptOutContactId,
        opt_out_all: optOutAll,
      });
      setBrokerOptOutOpen(false);
      setBrokerOptOutContactId(null);
      toast.success(optOutAll ? "All companies under this broker opted out" : "Contact opted out");
      fetchDetails();
    } catch {
      toast.error("Failed to opt out contact");
    }
  };

  const handleToggleOptOut = async (contactId, isBroker, isCurrentlyOptedOut) => {
    if (isCurrentlyOptedOut) {
      try {
        await axios.put(`${config.url.SMS_URL}/api/sms/contacts/${contactId}`, { is_opted_out: false });
        toast.success("Contact opted back in");
        fetchDetails();
      } catch {
        toast.error("Failed to opt in contact");
      }
    } else {
      handleOptOut(contactId, isBroker);
    }
  };

  const fetchAllContacts = async () => {
    setContactsLoading(true);
    try {
      const res = await axios.get(`${config.url.SMS_URL}/api/sms/contacts`);
      setAllContacts(res.data.contacts || res.data || []);
    } catch (err) {
      console.error("Failed to load contacts:", err);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleOpenContactPicker = (e, row) => {
    e.stopPropagation();
    setContactPickerAnchor(e.currentTarget);
    setContactPickerRow(row);
    fetchAllContacts();
  };

  const handleSelectContact = async (contact) => {
    if (!contactPickerRow) return;
    const prevRecipients = recipients;
    const { customer_name, customer_id } = contactPickerRow;

    // Close popover and optimistically update
    setContactPickerAnchor(null);
    setContactPickerRow(null);
    setRecipients(prev => prev.map(r =>
      r.customer_name === customer_name && r.customer_id === customer_id
        ? { ...r, contact_id: contact.id, person_name: contact.name, phone: contact.phone, email: contact.email }
        : r
    ));

    try {
      await axios.post(`${config.url.SMS_URL}/api/sms/contacts`, {
        customer_name, customer_id, name: contact.name, phone: contact.phone, email: contact.email, is_primary: true,
      });
      fetchDetails();
    } catch {
      setRecipients(prevRecipients);
      toast.error("Failed to assign contact");
    }
  };

  const commodityColumns = [
    { field: "name", headerName: "Commodity", flex: 1, minWidth: 120 },
    {
      field: "price",
      headerName: "Price",
      width: 120,
      renderCell: (params) => {
        if (editingPrice === params.row.name) {
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField
                size="small"
                value={editPriceValue}
                onChange={(e) => setEditPriceValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSavePrice(params.row.name); if (e.key === "Escape") setEditingPrice(null); }}
                autoFocus
                sx={{ width: 80, "& input": { fontSize: "0.75rem", py: 0.5, px: 1 } }}
                placeholder="0.00"
              />
              <IconButton size="small" onClick={() => handleSavePrice(params.row.name)}>
                <Check size={14} />
              </IconButton>
            </Stack>
          );
        }
        const p = params.value;
        return (
          <Typography
            variant="body2"
            onClick={(e) => {
              if (!isDatajoltUser) return;
              e.stopPropagation();
              setEditingPrice(params.row.name);
              setEditPriceValue(p ? String(p) : "");
            }}
            sx={{
              fontSize: "0.75rem",
              width: "100%",
              cursor: isDatajoltUser ? "pointer" : "default",
              "&:hover": isDatajoltUser ? { textDecoration: "underline" } : {},
            }}
          >
            {p ? `$${parseFloat(p).toFixed(2)}` : "-"}
          </Typography>
        );
      },
    },
  ];

  const recipientColumns = useMemo(() => [
    {
      field: "customer_name",
      headerName: "Customer",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "person_name",
      headerName: "Contact",
      flex: 1,
      minWidth: 80,
      renderCell: (params) => {
        const cn = params.row.customer_name;
        const msg = previewMessages.find(m => m.customer_name === cn || (m.broker_companies || []).includes(cn));
        const isBroker = params.row.is_broker || (msg && msg.is_broker);
        const isOptedOut = params.row.is_opted_out;
        const hasContact = !!params.row.contact_id && (!!params.row.person_name || !!params.row.phone || !!params.row.email);
        const editing = editingFieldRef.current;
        if (editing?.customer_name === cn && editing?.field === "person_name") {
          return (
            <InlineEditCell
              initialValue={params.value || ""}
              onSave={(v) => handleSaveRecipientField(cn, "person_name", v)}
              onCancel={() => updateEditingField(null)}
              placeholder="Contact name"
              prefix={isBroker ? <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#e65100", flexShrink: 0 }} /> : null}
            />
          );
        }
        return (
          <Box onClick={(e) => { e.stopPropagation(); hasContact ? updateEditingField({ customer_name: cn, field: "person_name" }) : handleOpenContactPicker(e, params.row); }} sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", cursor: "pointer" }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {isBroker && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#e65100", flexShrink: 0 }} />}
              {isOptedOut && (
                <Chip label="Opted out" size="small" sx={{ height: 16, fontSize: "0.55rem", fontWeight: 600, bgcolor: "#f5f5f5", color: "#999" }} />
              )}
              {!hasContact && (
                <Chip
                  label="Add +"
                  size="small"
                  sx={{ height: 16, fontSize: "0.55rem", fontWeight: 600, bgcolor: "#c8e6c9", color: "#2e7d32", cursor: "pointer", "&:hover": { bgcolor: "#a5d6a7" } }}
                />
              )}
              <Typography variant="body2" sx={{ fontSize: "0.75rem", "&:hover": { textDecoration: "underline" } }}>
                {hasContact ? (params.value || "-") : null}
              </Typography>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 140,
      renderCell: (params) => {
        const custName = params.row.customer_name;
        const editing = editingFieldRef.current;
        if (editing?.customer_name === custName && editing?.field === "phone") {
          return (
            <InlineEditCell
              initialValue={params.value || ""}
              onSave={(v) => handleSaveRecipientField(custName, "phone", v)}
              onCancel={() => updateEditingField(null)}
              placeholder="+1234567890"
              validate={(v) => v === "" || /^[\d\-+() ]*$/.test(v)}
            />
          );
        }
        return (
          <Box onClick={(e) => { e.stopPropagation(); updateEditingField({ customer_name: custName, field: "phone" }); }} sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", cursor: "pointer" }}>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", "&:hover": { textDecoration: "underline" } }}>
              {params.value || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 180,
      renderCell: (params) => {
        const custName = params.row.customer_name;
        const editing = editingFieldRef.current;
        if (editing?.customer_name === custName && editing?.field === "email") {
          return (
            <InlineEditCell
              initialValue={params.value || ""}
              onSave={(v) => handleSaveRecipientField(custName, "email", v)}
              onCancel={() => updateEditingField(null)}
              placeholder="email@example.com"
              validate={(v) => v === "" || /^[^\s]*$/.test(v)}
            />
          );
        }
        return (
          <Box onClick={(e) => { e.stopPropagation(); updateEditingField({ customer_name: custName, field: "email" }); }} sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", cursor: "pointer" }}>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", "&:hover": { textDecoration: "underline" } }}>
              {params.value || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "preferred_channel",
      headerName: "Channel",
      width: 90,
      sortable: false,
      renderCell: (params) => {
        const key = params.row.customer_name;
        const channel = recipientChannels[key] || "sms";
        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              const newChannel = recipientChannels[key] === "email" ? "sms" : "email";
              const updated = { ...recipientChannels, [key]: newChannel };
              setRecipientChannels(updated);
              const contactId = params.row.contact_id;
              if (contactId) {
                axios.put(`${config.url.SMS_URL}/api/sms/contacts/${contactId}`, { preferred_channel: newChannel }).catch(() => {});
              }
              setChannelLoadingRows(prev => new Set(prev).add(key));
              axios.post(`${config.url.SMS_URL}/api/sms/campaigns/${id}/preview`, { recipient_channels: updated })
                .then(res => setPreviewMessages(res.data.messages || []))
                .catch(() => {})
                .finally(() => setChannelLoadingRows(prev => { const next = new Set(prev); next.delete(key); return next; }));
            }}
            sx={{
              display: "inline-flex",
              borderRadius: "12px",
              overflow: "hidden",
              cursor: "pointer",
              border: "1px solid #e0e0e0",
              height: 22,
              fontSize: "0.6rem",
              fontWeight: 600,
            }}
          >
            <Box sx={{
              px: 0.75,
              display: "flex",
              alignItems: "center",
              bgcolor: channel !== "email" ? "#e8f5e9" : "transparent",
              color: channel !== "email" ? "#2e7d32" : "#bbb",
              transition: "all 0.15s",
            }}>SMS</Box>
            <Box sx={{
              px: 0.75,
              display: "flex",
              alignItems: "center",
              bgcolor: channel === "email" ? "#e3f2fd" : "transparent",
              color: channel === "email" ? "#1565c0" : "#bbb",
              transition: "all 0.15s",
            }}>Email</Box>
          </Box>
        );
      },
    },
    {
      field: "commodity_count",
      headerName: "Commodities",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        detailsLoading ? <CircularProgress size={12} /> : (
          <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {params.row.commodity_count ?? "-"}
          </Typography>
        )
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const hasContact = !!params.row.contact_id && (!!params.row.person_name || !!params.row.phone || !!params.row.email);
        return (
          <Stack direction="row" spacing={0} alignItems="center">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRecipientName(params.row.customer_name);
                setDrawerOpen(true);
              }}
              sx={{ p: 0.5 }}
              title="Preview message"
            >
              {(previewLoading || channelLoadingRows.has(params.row.customer_name)) ? <CircularProgress size={14} /> : <Eye size={14} />}
            </IconButton>
            {hasContact && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleOptOut(params.row.contact_id, params.row.is_broker, params.row.is_opted_out);
                  }}
                  sx={{ p: 0.5, opacity: params.row.is_opted_out ? 0.4 : 1 }}
                  title={params.row.is_opted_out ? "Opt back in" : "Opt out"}
                >
                  <UserX size={13} color={params.row.is_opted_out ? "#999" : undefined} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnassignContact(params.row.contact_id, params.row.customer_id);
                  }}
                  sx={{ p: 0.5 }}
                  title="Unassign contact"
                >
                  <Trash2 size={13} />
                </IconButton>
              </>
            )}
          </Stack>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [previewMessages, recipientChannels, detailsLoading, previewLoading, channelLoadingRows, isDatajoltUser, id]);

  const selectedRecipient = selectedRecipientName
    ? previewMessages.find(m =>
        m.customer_name === selectedRecipientName ||
        (m.broker_companies || []).includes(selectedRecipientName)
      ) || null
    : null;

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 64px)", px: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", margin: "12px 0", height: "45px" }}>
          <IconButton size="small" onClick={() => navigate("/crm")} sx={{ color: "text.secondary", mr: 1 }}>
            <ArrowLeft size={18} />
          </IconButton>
          <Skeleton variant="text" width={180} height={28} />
        </Box>
        <hr style={{ margin: "0" }} />
        <Stack direction="row" spacing={1.5} sx={{ mb: 1, mt: 1 }}>
          <Paper elevation={0} sx={{ flex: 0.7, minWidth: 0, borderRadius: 2, px: 2.5, py: 1.5, bgcolor: "#fff", border: "1px solid #f0f0f0", borderLeft: "3px solid #090F47" }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Skeleton variant="rounded" width={32} height={28} sx={{ borderRadius: 1 }} />
              <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary", letterSpacing: 0.3, textTransform: "uppercase" }}>COMMODITIES</Typography>
            </Stack>
          </Paper>
          <Paper elevation={0} sx={{ flex: 1, minWidth: 0, borderRadius: 2, px: 2.5, py: 1.5, bgcolor: "#fff", border: "1px solid #f0f0f0", borderLeft: "3px solid #090F47" }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Skeleton variant="rounded" width={32} height={28} sx={{ borderRadius: 1 }} />
              <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary", letterSpacing: 0.3, textTransform: "uppercase" }}>CUSTOMERS</Typography>
            </Stack>
          </Paper>
          <Paper elevation={0} sx={{ flex: 1, minWidth: 0, borderRadius: 2, px: 2.5, py: 1.5, bgcolor: "#fff", border: "1px solid #f0f0f0", borderLeft: "3px solid #090F47" }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Skeleton variant="rounded" width={32} height={28} sx={{ borderRadius: 1 }} />
              <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary", letterSpacing: 0.3, textTransform: "uppercase" }}>MESSAGES</Typography>
            </Stack>
          </Paper>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flex: 1, minHeight: 0 }}>
          <Paper variant="outlined" sx={{ flex: 1.1, minWidth: 0, borderRadius: 1 }}>
            <DataGrid rows={[]} columns={commodityColumns} loading density="compact" sx={dataGridSx} slotProps={{ loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" } }} />
          </Paper>
          <Paper variant="outlined" sx={{ flex: 3, minWidth: 0, borderRadius: 1 }}>
            <DataGrid rows={[]} columns={recipientColumns} loading density="compact" sx={dataGridSx} slotProps={{ loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" } }} />
          </Paper>
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <Helmet title={`Datajolt | ${campaign?.name || "Campaign Details"}`} />
      <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 64px)" }}>
        <Box sx={{ px: 1, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          {/* Header bar */}
          <Box sx={{ display: "flex", alignItems: "center", margin: "12px 0", height: "45px" }}>
            <IconButton size="small" onClick={() => navigate("/crm")} sx={{ color: "text.secondary", mr: 1 }}>
              <ArrowLeft size={18} />
            </IconButton>
            <h4 style={{ marginBottom: 0 }}>
              <b style={{ color: "#090E47" }}>{campaign?.name || "Campaign"}</b>
            </h4>
            <Paper
              variant="outlined"
              sx={{
                ml: 1.5,
                px: 1.5,
                py: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                borderRadius: 1,
                bgcolor: campaign?.next_run ? "#e8f5e9" : "#f5f5f5",
                borderColor: campaign?.next_run ? "#a5d6a7" : "#e0e0e0",
              }}
            >
              <Clock size={13} color={campaign?.next_run ? "#2e7d32" : "#888"} />
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: campaign?.next_run ? "#2e7d32" : "text.secondary" }}>
                Next run: {campaign?.next_run ? new Date(campaign.next_run).toLocaleString() : "Not scheduled"}
              </Typography>
            </Paper>
            <Box sx={{ display: "flex", marginLeft: "auto", alignItems: "center", gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 160, "& .MuiInputLabel-root": { fontSize: "0.75rem", top: -4 }, "& .MuiInputLabel-shrink": { top: 2 } }}>
                <InputLabel>SMS Template</InputLabel>
                <Select
                  value={smsTemplateId || ""}
                  label="SMS Template"
                  onChange={(e) => handleTemplateChange("sms", e.target.value)}
                  sx={{ fontSize: "0.75rem", height: 28, "& .MuiSelect-select": { py: 0.25, px: 1 } }}
                >
                  <MenuItem value="" sx={{ fontSize: "0.75rem" }}><em>None</em></MenuItem>
                  {templates.filter(t => t.type !== "email").map(t => (
                    <MenuItem key={t.id} value={t.id} sx={{ fontSize: "0.75rem" }}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160, "& .MuiInputLabel-root": { fontSize: "0.75rem", top: -4 }, "& .MuiInputLabel-shrink": { top: 2 } }}>
                <InputLabel>Email Template</InputLabel>
                <Select
                  value={emailTemplateId || ""}
                  label="Email Template"
                  onChange={(e) => handleTemplateChange("email", e.target.value)}
                  sx={{ fontSize: "0.75rem", height: 28, "& .MuiSelect-select": { py: 0.25, px: 1 } }}
                >
                  <MenuItem value="" sx={{ fontSize: "0.75rem" }}><em>None</em></MenuItem>
                  {templates.filter(t => t.type === "email").map(t => (
                    <MenuItem key={t.id} value={t.id} sx={{ fontSize: "0.75rem" }}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 80, "& .MuiInputLabel-root": { fontSize: "0.75rem", top: -4 }, "& .MuiInputLabel-shrink": { top: 2 } }}>
                <InputLabel>Rank ≤</InputLabel>
                <Select
                  value={priorityRank}
                  label="Rank ≤"
                  onChange={(e) => handlePriorityRankChange(e.target.value)}
                  sx={{ fontSize: "0.75rem", height: 28, "& .MuiSelect-select": { py: 0.25, px: 1 } }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 10, 15, 20].map(v => (
                    <MenuItem key={v} value={v} sx={{ fontSize: "0.75rem" }}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          {/* KPI boxes */}
          <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
            {/* COMMODITIES */}
            <Paper elevation={0} sx={{ flex: 0.7, minWidth: 0, borderRadius: 2, px: 2.5, py: 1.5, bgcolor: "#fff", border: "1px solid #f0f0f0", borderLeft: "3px solid #090F47" }}>
              {rankLoading ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Skeleton variant="rounded" width={32} height={28} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="text" width={100} height={20} />
                </Stack>
              ) : (() => {
                const missingPriceCount = commodities.filter(c => selectedCommodityIds.ids?.has(c.id) && !c.price).length;
                return (
                  <>
                    <Stack direction="row" alignItems="baseline" spacing={1}>
                      <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: "#090F47", lineHeight: 1 }}>
                        {selectedCommodityIds.ids?.size ?? 0}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary", letterSpacing: 0.3, textTransform: "uppercase" }}>
                        Commodities
                      </Typography>
                    </Stack>
                    {missingPriceCount > 0 && (
                      <Typography
                        onClick={() => setShowMissingPricesOnly(prev => !prev)}
                        sx={{
                          fontSize: "0.65rem",
                          color: showMissingPricesOnly ? "#090F47" : "#e65100",
                          cursor: "pointer",
                          fontWeight: 600,
                          mt: 0.5,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {showMissingPricesOnly ? "Show all" : `${missingPriceCount} missing prices`}
                      </Typography>
                    )}
                  </>
                );
              })()}
            </Paper>

            {/* CUSTOMERS */}
            <Paper elevation={0} sx={{ flex: 1, minWidth: 0, borderRadius: 2, px: 2.5, py: 1.5, bgcolor: "#fff", border: "1px solid #f0f0f0", borderLeft: "3px solid #090F47" }}>
              {rankLoading ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Skeleton variant="rounded" width={32} height={28} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="text" width={100} height={20} />
                </Stack>
              ) : (() => {
                const missingContactCount = recipients.filter(r => {
                  if (!selectedRecipientIds.ids?.has(r.id)) return false;
                  const ch = recipientChannels[r.customer_name] || r.preferred_channel || "sms";
                  return !r.contact_id || (ch === "sms" && !r.phone) || (ch === "email" && !r.email);
                }).length;
                return (
                  <>
                    <Stack direction="row" alignItems="baseline" spacing={1}>
                      <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: "#090F47", lineHeight: 1 }}>
                        {selectedRecipientIds.ids?.size ?? 0}
                      </Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary", letterSpacing: 0.3, textTransform: "uppercase" }}>
                        Customers
                      </Typography>
                    </Stack>
                    {missingContactCount > 0 && (
                      <Typography
                        onClick={() => setShowMissingContactsOnly(prev => !prev)}
                        sx={{
                          fontSize: "0.65rem",
                          color: showMissingContactsOnly ? "#090F47" : "#e65100",
                          cursor: "pointer",
                          fontWeight: 600,
                          mt: 0.5,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {showMissingContactsOnly ? "Show all" : `${missingContactCount} missing contacts`}
                      </Typography>
                    )}
                  </>
                );
              })()}
            </Paper>

            {/* MESSAGES */}
            <Paper elevation={0} sx={{ flex: 1, minWidth: 0, borderRadius: 2, px: 2.5, py: 1.5, bgcolor: "#fff", border: "1px solid #f0f0f0", borderLeft: "3px solid #090F47" }}>
              {rankLoading ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Skeleton variant="rounded" width={32} height={28} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="text" width={100} height={20} />
                </Stack>
              ) : (() => {
                const selectedCustomerNames = new Set(
                  recipients.filter(r => selectedRecipientIds.ids?.has(r.id)).map(r => r.customer_name)
                );
                const activeMessages = previewMessages.filter(m => {
                  if (m.is_broker && m.broker_companies?.length) {
                    return m.broker_companies.some(cn => selectedCustomerNames.has(cn));
                  }
                  return selectedCustomerNames.has(m.customer_name);
                });
                const smsCount = activeMessages.filter(m => m.channel !== 'email').length;
                const emailCount = activeMessages.filter(m => m.channel === 'email').length;
                return (
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: "#090F47", lineHeight: 1 }}>
                      {smsCount + emailCount}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary", letterSpacing: 0.3, textTransform: "uppercase" }}>
                      Messages
                    </Typography>
                    <Typography sx={{ fontWeight: 500, fontSize: "0.7rem", color: "#999" }}>
                      {smsCount} sms &middot; {emailCount} email
                    </Typography>
                  </Stack>
                );
              })()}
            </Paper>
          </Stack>

          {/* Two-panel layout: Commodities | Recipients */}
          <Stack direction="row" spacing={1} sx={{ flex: 1, minHeight: 400 }}>
            {/* LEFT: Commodities */}
            <Paper variant="outlined" sx={{ flex: 1.1, minWidth: 280, borderRadius: 1, display: "flex", flexDirection: "column" }}>
              <DataGrid
                rows={showMissingPricesOnly ? commodities.filter(c => !c.price) : commodities}
                columns={commodityColumns}
                density="compact"
                checkboxSelection
                disableRowSelectionOnClick
                disableColumnSorting
                keepNonExistentRowsSelected
                loading={rankLoading}
                slots={{ columnMenuIcon: () => <Search size={12} />, columnMenu: AutoFilterMenu }}
                slotProps={{ loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" } }}
                rowSelectionModel={selectedCommodityIds}
                onRowSelectionModelChange={async (ids) => {
                  // Determine which commodities were toggled
                  const prev = selectedCommodityIds.ids || new Set();
                  const next = ids.ids || new Set();
                  const promises = [];
                  // Newly unticked
                  for (const cId of prev) {
                    if (!next.has(cId)) {
                      const com = commodities.find(c => c.id === cId);
                      if (com) promises.push(axios.post(`${config.url.SMS_URL}/api/sms/commodities/override`, { name: com.name, is_available: false, campaign_id: id }));
                    }
                  }
                  // Newly re-ticked
                  for (const cId of next) {
                    if (!prev.has(cId)) {
                      const com = commodities.find(c => c.id === cId);
                      if (com) promises.push(axios.post(`${config.url.SMS_URL}/api/sms/commodities/override`, { name: com.name, is_available: true, campaign_id: id }));
                    }
                  }
                  setSelectedCommodityIds(ids);
                  if (promises.length > 0) {
                    setDetailsLoading(true);
                    await Promise.all(promises);
                    await fetchDetails();
                    setDetailsLoading(false);
                  }
                }}
                getRowHeight={autoRowHeight}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                  sorting: { sortModel: [{ field: "name", sort: "asc" }] },
                }}
                pageSizeOptions={[10, 25, 50]}
                sx={dataGridSx}
              />
            </Paper>

            {/* RIGHT: Recipients */}
            <Paper variant="outlined" sx={{ flex: 3, minWidth: 0, borderRadius: 1, display: "flex", flexDirection: "column" }}>
              <DataGrid
                rows={showMissingContactsOnly ? recipients.filter(r => {
                  const ch = recipientChannels[r.customer_name] || r.preferred_channel || "sms";
                  return !r.contact_id || (ch === "sms" && !r.phone) || (ch === "email" && !r.email);
                }) : recipients}
                columns={recipientColumns}
                density="compact"
                checkboxSelection
                disableRowSelectionOnClick
                disableColumnSorting
                keepNonExistentRowsSelected
                loading={rankLoading}
                slots={{ columnMenuIcon: () => <Search size={12} />, columnMenu: AutoFilterMenu, footer: RecipientsFooter }}
                slotProps={{ loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" } }}
                rowSelectionModel={selectedRecipientIds}
                onRowSelectionModelChange={(ids) => setSelectedRecipientIds(ids)}
                getRowClassName={getMissingContactRowClass}
                getRowHeight={autoRowHeight}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[10, 25, 50]}
                sx={dataGridSx}
              />
            </Paper>
          </Stack>
        </Box>
      </Box>

      {/* Message Preview Modal */}
      <Dialog open={drawerOpen} onClose={() => setDrawerOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: "70vh" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1, px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Message Preview</Typography>
            {selectedRecipient && (
              <>
                <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 400 }}>—</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedRecipient.is_broker
                    ? (selectedRecipient.person_name || selectedRecipient.customer_name || "Broker")
                    : (selectedRecipient.customer_name || selectedRecipient.person_name || "Customer")}
                  {!selectedRecipient.is_broker && selectedRecipient.person_name && selectedRecipient.customer_name && selectedRecipient.person_name !== selectedRecipient.customer_name && (
                    <Typography component="span" sx={{ fontWeight: 400, fontSize: 12, color: "text.secondary", ml: 0.5 }}>
                      ({selectedRecipient.person_name})
                    </Typography>
                  )}
                </Typography>
              </>
            )}
            {selectedRecipient?.is_broker && (
              <Chip label="Broker" size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 600, bgcolor: "#fff3e0", color: "#e65100" }} />
            )}
          </Box>
          <IconButton size="small" onClick={() => setDrawerOpen(false)}><X size={16} /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {selectedRecipientName && (previewLoading || !selectedRecipient) ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>Loading preview...</Typography>
            </Box>
          ) : selectedRecipient ? (
            <>
              {/* Broker companies */}
              {selectedRecipient.is_broker && selectedRecipient.broker_companies?.length > 0 && (
                <Box sx={{ bgcolor: "#f6f6f6", px: 2, py: 0.75, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selectedRecipient.broker_companies.map((co) => (
                    <Chip key={co} label={co} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.55rem", "& .MuiChip-label": { px: 0.5 } }} />
                  ))}
                </Box>
              )}
              {/* Missing contact warning */}
              {selectedRecipient.channel === 'email' && !selectedRecipient.email && (
                <Box sx={{ bgcolor: '#fff3e0', px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 500 }}>
                    No email address — this message won't be sent
                  </Typography>
                </Box>
              )}
              {selectedRecipient.channel !== 'email' && selectedRecipient.missing_phone && (
                <Box sx={{ bgcolor: '#fff3e0', px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 500 }}>
                    No phone number — this message won't be sent
                  </Typography>
                </Box>
              )}
              {/* Message body */}
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", minHeight: 200 }}>
                {selectedRecipient.channel === "email" ? (
                  <>
                    <Box sx={{ mb: 1, pb: 1, borderBottom: "1px solid #e0e0e0", textAlign: "left" }}>
                      <Typography variant="caption" color="text.secondary">
                        Subject: <b>{selectedRecipient.subject || "(no subject)"}</b>
                      </Typography>
                    </Box>
                    {selectedRecipient.body_html ? (
                      <Box
                        sx={{
                          fontSize: 14, lineHeight: 1.5, textAlign: "left",
                          "& table": { borderCollapse: "collapse", width: "100%", my: 1 },
                          "& td, & th": { border: "1px solid #ddd", px: 1.5, py: 0.75, textAlign: "left", fontSize: 13 },
                          "& th": { bgcolor: "#f5f5f5", fontWeight: 600 },
                          "& h4": { fontSize: 13, fontWeight: 600 },
                        }}
                        dangerouslySetInnerHTML={{ __html: selectedRecipient.body_html }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5, textAlign: "left" }}>
                        {selectedRecipient.message}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        alignSelf: "flex-end",
                        marginTop: "auto",
                        maxWidth: "85%",
                        bgcolor: "#34C759",
                        color: "white",
                        borderRadius: "18px 18px 4px 18px",
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.4, textAlign: "left" }}>
                        {selectedRecipient.message}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ alignSelf: "flex-end", mt: 0.5, color: "text.secondary", fontSize: 11 }}>
                      {(selectedRecipient.message || "").length} characters
                    </Typography>
                  </>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
              <Typography variant="body2" color="text.secondary">No preview available for this recipient</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Contact Dialog */}
      <CreateContactDialog
        open={createContactOpen}
        onClose={() => setCreateContactOpen(false)}
        row={createContactRow}
        onSave={handleCreateContact}
      />

      {/* Contact Picker Popover */}
      <ContactPickerPopover
        anchorEl={contactPickerAnchor}
        contacts={allContacts}
        loading={contactsLoading}
        onClose={() => { setContactPickerAnchor(null); setContactPickerRow(null); }}
        onSelect={handleSelectContact}
        onCreateNew={() => {
          setContactPickerAnchor(null);
          setCreateContactRow(contactPickerRow);
          setContactPickerRow(null);
          setCreateContactOpen(true);
        }}
      />

      {/* Broker Opt-Out Confirmation Dialog */}
      <Dialog
        open={brokerOptOutOpen}
        onClose={() => setBrokerOptOutOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            overflow: "hidden",
            background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
            border: "1px solid rgba(0,0,0,0.08)",
            transition: "all 0.3s ease-in-out"
          }
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          position="relative"
          padding="16px 24px"
          borderBottom="1px solid rgba(0,0,0,0.05)"
          sx={{ background: "rgba(255,243,224,0.6)" }}
        >
          <DialogTitle
            padding="0 !important"
            sx={{
              color: "#e65100",
              fontWeight: 800,
              fontSize: "11px",
              display: "flex",
              alignItems: "center",
              "::before": {
                content: '""',
                display: "inline-block",
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#e65100",
                marginRight: "10px",
                animation: "pulse 1.5s infinite",
                "@keyframes pulse": {
                  "0%": { boxShadow: "0 0 0 0 rgba(230, 81, 0, 0.7)" },
                  "70%": { boxShadow: "0 0 0 10px rgba(230, 81, 0, 0)" },
                  "100%": { boxShadow: "0 0 0 0 rgba(230, 81, 0, 0)" }
                }
              }
            }}
          >
            Opt Out Broker
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setBrokerOptOutOpen(false)}
            sx={{
              position: "absolute",
              right: 16,
              top: 10,
              color: "#495057",
              "&:hover": {
                background: "rgba(0,0,0,0.05)",
                transform: "rotate(90deg)",
                transition: "transform 0.3s ease"
              }
            }}
          >
            <X size={16} />
          </IconButton>
        </Stack>
        <DialogContent sx={{ padding: "24px 28px" }}>
          <Typography
            textAlign="center"
            color="#4a4a4a"
            sx={{ fontSize: "11px", lineHeight: 1.6 }}
          >
            This contact is a broker. Opt out all companies under this broker, or just this one?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", gap: "12px" }}>
          <Button
            size="small"
            onClick={() => setBrokerOptOutOpen(false)}
            sx={{
              fontSize: "11px",
              borderRadius: "8px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              transition: "all 0.2s",
              padding: "8px 16px",
              color: "#5a6268",
              background: "linear-gradient(45deg, #f5f5f5, #e9ecef)",
              border: "1px solid #dee2e6",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                background: "linear-gradient(45deg, #e9ecef, #dee2e6)",
              }
            }}
          >
            <X size={14} style={{ marginRight: "6px" }} /> Cancel
          </Button>
          <Button
            size="small"
            onClick={() => handleBrokerOptOutConfirm(false)}
            sx={{
              fontSize: "11px",
              borderRadius: "8px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              transition: "all 0.2s",
              padding: "8px 16px",
              color: "#5a6268",
              border: "1px solid #dee2e6",
              background: "transparent",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                background: "rgba(0,0,0,0.03)",
              }
            }}
          >
            Just this one
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={() => handleBrokerOptOutConfirm(true)}
            sx={{
              fontSize: "11px",
              borderRadius: "8px",
              fontWeight: 600,
              textTransform: "none",
              padding: "8px 20px",
              transition: "all 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 8px rgba(230,81,0,0.25)"
              }
            }}
          >
            All companies
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignDetails;
